import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { dataSetSeasons } from '../../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../../services/appFunctionsService';
import { ICommodity } from '../../../../@types/model/masterData/commodity/commodity';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import SeasonForm from './form/SeasonForm';
import SeasonHttpService from '../../../../services/http/masterData/seasonHttpService';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../../@types/other';
import { setSeasonMasterDataIndexedDB, syncMasterData } from '../../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import SeasonModelHelper from '../../../../@types/model/masterData/season/seasonModelHelper';
import { RightGrantsEnum } from '../../../../@types/enum/rightGrantsEnum';
import { ISeason } from '../../../../@types/model/masterData/season/season';
import { ISeasonFormValues } from '../../../../@types/model/masterData/season/seasonFormValues';
import UserHelper from '../../../../services/helper/userHelper';

interface ISeasonScreenProps {
    selectedCommodity : ICommodity | null;
}

const SeasonScreen = (props : ISeasonScreenProps) : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const seasons = useAppSelector(x => x.masterData.seasons);
    const commodities = useAppSelector(x => x.masterData.commodities);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedSeason, setSelectedSeason] = useState<ISeason | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingSeason, setDeletingSeason] = useState<ISeason | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await SeasonHttpService.getSeasonData();
                dispatch(dataSetSeasons(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading seasons.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : ISeasonFormValues) => {
        setIsLoading(true);

        try {
            const upsert = SeasonModelHelper.createUpsert(values);
            const res = await SeasonHttpService.addOrUpdateSeason(upsert);
            const updatedList = upsertArrayElement(seasons, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetSeasons(updatedList));
            await setSeasonMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedSeason ? 'Season updated successfully.' : 'Season added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating season data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeSeason = async () => {
        if (!deletingSeason) return;
        const updatedSeason = { ...deletingSeason, isActive: false };
        setIsLoading(true);
        try {
            const res = await SeasonHttpService.deleteSeason(updatedSeason.id);
            const updatedList = upsertArrayElement(seasons, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetSeasons(updatedList));
            await setSeasonMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Season deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting season.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedSeason(null);
    };

    const edit = (selectedRow : ISeason) => {
        setSelectedSeason(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ISeason) : void => {
        setDeletingSeason(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingSeason(null);
    };

    const addButtonTooltip = () : string => {
        let message = 'Add';

        if (!userHasRights.addRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Season', RightGrantsEnum.Add);
        } else if (!props.selectedCommodity) {
            message = 'Commodity Not Selected';
        }

        return message;
    };

    const deleteButtonTooltip = (row : ISeason) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Season', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const getCommodityName = useCallback((id : number) => {
        const commodity = commodities.find(x => x.id === id);
        return commodity ? commodity.name : '';
    }, [commodities]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return SeasonModelHelper.createFormValues(selectedSeason, commodities, props.selectedCommodity);
        }
    }, [selectedSeason, isDialogOpen, commodities, props.selectedCommodity]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo(() => {
        return props.selectedCommodity ? seasons.filter(x => x.commodityId === props.selectedCommodity?.id) : seasons;
    }, [props.selectedCommodity, seasons]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <>
            <Card className='fdc hfill'>
                <CustomTable<ISeason>
                        
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || !props.selectedCommodity || isLoading}
                    addTooltip={addButtonTooltip()}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Season', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : ISeason) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}
                
                    enableSorting
                    enableFiltering
                    enablePagination
                    fitWidthToPage
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Start Date', field: 'startDate', width: 200, type: 'date', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'End Date', field: 'endDate', width: 200, type: 'date', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Commodity', field: 'commodityId', formatFunction: getCommodityName, width: 150, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={rows}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={340}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedSeason ? 'Edit Season' : 'Add Season'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <SeasonForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Season'}
                open={!!deletingSeason}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeSeason}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </>
    );
};

export default SeasonScreen;
